import { osmEntity } from './entity';
import { geoAngle, geoExtent, geoVecAdd, geoVecLength, geoVecNormalize, geoVecSubtract } from '../geo';
import { utilArrayUniqBy } from '../util';
import { osmShouldRenderDirection } from './tags';

export const cardinal = {
    north: 0,               n: 0,
    northnortheast: 22,     nne: 22,
    northeast: 45,          ne: 45,
    eastnortheast: 67,      ene: 67,
    east: 90,               e: 90,
    eastsoutheast: 112,     ese: 112,
    southeast: 135,         se: 135,
    southsoutheast: 157,    sse: 157,
    south: 180,             s: 180,
    southsouthwest: 202,    ssw: 202,
    southwest: 225,         sw: 225,
    westsouthwest: 247,     wsw: 247,
    west: 270,              w: 270,
    westnorthwest: 292,     wnw: 292,
    northwest: 315,         nw: 315,
    northnorthwest: 337,    nnw: 337
};

export const SIDE_TAGS = [
    'side',
    'railway:signal:position',
    // railway:turnout_side has special handling, since it's not relative to the way direction
];

export const SIDES = new Set(['left', 'right', 'both']);

export const SIDE_ANGLE_OFFSET = { left: 180, right: 0 };

/**
 * @typedef {typeof prototype & iD.AbstractEntity} OsmNode
 * @returns {OsmNode}
 */
export function osmNode() {
    if (!(this instanceof osmNode)) {
        return new osmNode(...arguments);
    }
    this.initialize(arguments);
}

osmEntity.node = osmNode;

osmNode.prototype = Object.create(osmEntity.prototype);

const prototype = {
    type: /** @type {'node'} */ ('node'),
    loc: /** @type {Vec2} */ ([9999, 9999]),

    extent: function() {
        return new geoExtent(this.loc);
    },


    geometry: function(graph) {
        return graph.transient(this, 'geometry', function() {
            return graph.isPoi(this) ? 'point' : 'vertex';
        });
    },


    move: function(loc) {
        return this.update({loc: loc});
    },


    isDegenerate: function() {
        return !(
            Array.isArray(this.loc) && this.loc.length === 2 &&
            this.loc[0] >= -180 && this.loc[0] <= 180 &&
            this.loc[1] >= -90 && this.loc[1] <= 90
        );
    },


    // Inspect tags and geometry to determine which direction(s) this node/vertex points
    directions: function(resolver, projection) {
        /** @type {{ type: 'side' | 'turnout_side' | 'direction'; value: string }[]} */
        const rawValues = [];

        // which tag to use?
        if (this.isHighwayIntersection(resolver) && (this.tags.stop || '').toLowerCase() === 'all') {
            // all-way stop tag on a highway intersection
            rawValues.push({
                type: 'direction',
                value: 'all',
            });
        } else if (this.tags['railway:turnout_side'] && resolver.parentWays(this).length > 1) {
            rawValues.push({
                type: 'turnout_side',
                value: this.tags['railway:turnout_side'].toLowerCase(),
            });
        } else {
            // generic side tag

            // for `highway=cyclist_waiting_aid`, `side` (i.e. from cyclist's perspective) will be ambiguous
            // unless the `direction` is explicitly specified (even on one-way roads).
            const isSideTagAmbiguous = (
                this.tags.highway === 'cyclist_waiting_aid' &&
                this.tags.side !== 'both' &&
                this.tags.direction !== 'forward' &&
                this.tags.direction !== 'backward'
            );

            const sideTag = SIDE_TAGS.map(key => this.tags[key]).find(Boolean);
            if (SIDES.has(sideTag?.toLowerCase()) && !isSideTagAmbiguous) {
                rawValues.push({
                    type: 'side',
                    value: sideTag?.toLowerCase(),
                });
            }

            // generic direction tag
            let val = (this.tags.direction || '').toLowerCase();

            // better suffix-style direction tag
            const re = /:direction$/i;
            for (const key of Object.keys(this.tags)) {
                if (re.test(key)) {
                    val = this.tags[key].toLowerCase();
                    break;
                }
            }
            for (const value of val.split(';')) {
                rawValues.push({ type: 'direction', value });
            }
        }

        if (!rawValues.length) return [];


        /** @type {{ type: 'side' | 'direction'; angle: number }[]} */
        const results = [];

        const neighborNodeReducer = (neighbor, { lookBackward = true, lookForward = true } = {}) => function(collection, { nodes }) {
            nodes.forEach((_, i) => {
                if (nodes[i] !== neighbor.id) return;  // not a match of current entity

                if (lookForward && i > 0) {
                    collection[nodes[i - 1]] = true;  // look back to prev node
                }
                if (lookBackward && i < nodes.length - 1) {
                    collection[nodes[i + 1]] = true;  // look ahead to next node
                }
            });
            return collection;
        };

        rawValues.forEach(({ type, value: v }) => {
            // swap cardinal for numeric directions
            if (cardinal[v] !== undefined) {
                v = cardinal[v];
            }

            // numeric direction - just add to results
            if (v !== '' && !isNaN(+v)) {
                results.push({ type: 'direction', angle: +v });
                return;
            }

            if (type === 'turnout_side') {
                if (SIDE_ANGLE_OFFSET[v] === undefined) return;

                const branchVectors = resolver.parentWays(this)
                    .filter(way => way.tags.railway && way.geometry(resolver) === 'line')
                    .reduce(neighborNodeReducer(this), {});

                const ids = Object.keys(branchVectors);
                // the turnout side tag is only meaningfully defined for switches with 3 branches - one incoming and two outgoing
                if (ids.length !== 3) return;

                ids.forEach(id => branchVectors[id] = geoVecNormalize(geoVecSubtract(projection(resolver.entity(id).loc), projection(this.loc))));

                const sortedIds = ids.map(id => {
                    const otherVectorSum = ids
                        .filter(n => n !== id)
                        .map(n => branchVectors[n])
                        .reduce(geoVecAdd, [0, 0]);
                    return { id, alignment: geoVecLength(otherVectorSum) };
                }).sort((a, b) => b.alignment - a.alignment);

                // don't attempt to interpret the turnout side if the angles of the branches are too similar
                const MIN_ALIGNMENT_RATIO = 2;
                if (sortedIds[0].alignment < MIN_ALIGNMENT_RATIO * sortedIds[1].alignment || !sortedIds[0].id) return;

                results.push({
                    type: 'side',
                    angle: (geoAngle(this, resolver.entity(sortedIds[0].id), projection) * (180 / Math.PI)) + SIDE_ANGLE_OFFSET[v]
                });
            } else {

                const isSide = type === 'side' && SIDES.has(v);

                // In case of `highway=cyclist_waiting_aid`, if the `side` is to be shown, then
                // it will be explicitly specified by the `direction` from the cyclist's perspective (not way's perspective)
                const isSideFlipped = isSide &&
                    this.tags.highway === 'cyclist_waiting_aid' &&
                    this.tags.direction === 'backward';

                // string direction - inspect parent ways
                const lookBackward =
                    (this.tags['traffic_sign:backward'] || v === (isSide ? 'left' : 'backward') || v === 'both' || v === 'all');
                const lookForward =
                    (this.tags['traffic_sign:forward'] || v === (isSide ? 'right' : 'forward') || v === 'both' || v === 'all');

                if (!lookForward && !lookBackward) return;

                const nodeIds = resolver.parentWays(this)
                    .filter(way => osmShouldRenderDirection(this.tags, way.tags))
                    .reduce(neighborNodeReducer(this, { lookForward, lookBackward }), {});

                Object.keys(nodeIds).forEach(function(nodeId) {
                    // +90 because geoAngle returns angle from X axis, not Y (north)
                    results.push({
                        type: isSide ? 'side' : 'direction',
                        angle: (geoAngle(this, resolver.entity(nodeId), projection) * (180 / Math.PI)) + (isSide ? (isSideFlipped ? 180 : 0) : 90)
                    });
                }, this);
            }
        }, this);

        return utilArrayUniqBy(results, item => item.type + item.angle);
    },

    isCrossing: function(){
        return this.tags.highway === 'crossing' ||
               this.tags.railway && this.tags.railway.indexOf('crossing') !== -1;
    },

    isEndpoint: function(resolver) {
        return resolver.transient(this, 'isEndpoint', function() {
            var id = this.id;
            return resolver.parentWays(this).filter(function(parent) {
                return !parent.isClosed() && !!parent.affix(id);
            }).length > 0;
        });
    },


    isConnected: function(resolver) {
        return resolver.transient(this, 'isConnected', function() {
            var parents = resolver.parentWays(this);

            if (parents.length > 1) {
                // vertex is connected to multiple parent ways
                for (var i in parents) {
                    if (parents[i].geometry(resolver) === 'line' &&
                        parents[i].hasInterestingTags()) return true;
                }
            } else if (parents.length === 1) {
                var way = parents[0];
                var nodes = way.nodes.slice();
                if (way.isClosed()) { nodes.pop(); }  // ignore connecting node if closed

                // return true if vertex appears multiple times (way is self intersecting)
                return nodes.indexOf(this.id) !== nodes.lastIndexOf(this.id);
            }

            return false;
        });
    },


    parentIntersectionWays: function(resolver) {
        return resolver.transient(this, 'parentIntersectionWays', function() {
            return resolver.parentWays(this).filter(function(parent) {
                return (parent.tags.highway ||
                    parent.tags.waterway ||
                    parent.tags.railway ||
                    parent.tags.aeroway) &&
                    parent.geometry(resolver) === 'line';
            });
        });
    },


    isIntersection: function(resolver) {
        return this.parentIntersectionWays(resolver).length > 1;
    },


    isHighwayIntersection: function(resolver) {
        return resolver.transient(this, 'isHighwayIntersection', function() {
            return resolver.parentWays(this).filter(function(parent) {
                return parent.tags.highway && parent.geometry(resolver) === 'line';
            }).length > 1;
        });
    },


    isOnAddressLine: function(resolver) {
        return resolver.transient(this, 'isOnAddressLine', function() {
            return resolver.parentWays(this).filter(function(parent) {
                return parent.tags.hasOwnProperty('addr:interpolation') &&
                    parent.geometry(resolver) === 'line';
            }).length > 0;
        });
    },


    asJXON: function(changeset_id) {
        var r = {
            node: {
                '@id': this.osmId(),
                '@lon': this.loc[0],
                '@lat': this.loc[1],
                '@version': (this.version || 0),
                tag: Object.keys(this.tags).map(function(k) {
                    return { keyAttributes: { k: k, v: this.tags[k] } };
                }, this)
            }
        };
        if (changeset_id) r.node['@changeset'] = changeset_id;
        return r;
    },


    asGeoJSON: function() {
        return {
            type: 'Point',
            coordinates: this.loc
        };
    }
};
Object.assign(osmNode.prototype, prototype);
